import React, {useEffect, useRef} from 'react';
import {
    Button,
    Container,
    LinearProgress,
    TextField, Select, InputLabel, FormControl
} from '@material-ui/core';
import {Line} from 'react-chartjs-2';
import styles from '../styles/pages/Sentiment.module.css';
import {
    emptySeries,
    getAverageTweetsSentimentData, getTweetBankFilterData,
    getTweetsSentimentData,
    Series,
    TweetBankFilterData
} from '../rest/client';
import {ChartData, ChartDataSets, ChartOptions, ChartPoint} from 'chart.js';
import * as _ from 'lodash';
import MenuItem from "@material-ui/core/MenuItem";
import ArticleSentimentStatsTable, {ArticleSentimentStatsRow} from "../controls/ArticleSentimentStasTable";
import useMediaQuery from "../hooks/useMediaQuery";
import breakpoints from "../styles/Breakpoints.module.css";
import {zoomOptions} from '../helpers/chartZoom';
import 'chartjs-plugin-zoom';


const TweetsSentiment: React.FC = () => {
    const [sentimentData, setSentimentData] = React.useState<Series>(emptySeries);
    const [sentimentDataByKeyword, setSentimentDataByKeyword] = React.useState<Series>(emptySeries);
    const [allTweetBanks, setAllTweetBanks] = React.useState<TweetBankFilterData[]>([]);
    const [groupBy, setGroupBy] = React.useState<string>("day");
    const [selectedTweetBankId, setSelectedTweetBankId] = React.useState<number>(0);
    const [keyword, setKeyword] = React.useState<string>();
    const [startMonth, setStartMonth] = React.useState<number>(1);
    const [startYear, setStartYear] = React.useState<number>(2015);
    const [endYear, setEndYear] = React.useState<number>(2020);
    const [error, setError] = React.useState<string>();
    const [inProgress, setInProgress] = React.useState(false);
    const isMobileScreen = useMediaQuery(breakpoints.sm);

    const months = [
        {id: 1, name: 'January'},
        {id: 2, name: 'February'},
        {id: 3, name: 'March'},
        {id: 4, name: 'April'},
        {id: 5, name: 'May'},
        {id: 6, name: 'June'},
        {id: 7, name: 'July'},
        {id: 8, name: 'August'},
        {id: 9, name: 'September'},
        {id: 10, name: 'October'},
        {id: 11, name: 'November'},
        {id: 12, name: 'December'}
    ]

    useEffect(() => {
        getTweetBankFilterData().then(data => {
            let sorted = data.sort((a, b) => a.phraseEn.localeCompare(b.phraseEn))
            setAllTweetBanks(sorted)
            if (sorted.length > 0) {
                setSelectedTweetBankId(sorted[0].id)
            }
        })
    // eslint-disable-next-line
    }, [])

    useEffect(() => {
        if (selectedTweetBankId > 0) {
            fetchSentimentData();
        }
        if (keyword) {
            fetchSentimentDataByKeyword()
        }
    // eslint-disable-next-line
    }, [selectedTweetBankId, groupBy, startMonth, startYear, endYear])


    const handleTweetBankChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let tweetBankId = Number(event.target.value);
        setSelectedTweetBankId(tweetBankId)
    };

    const handleGroupByChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
       setGroupBy(String(event.target.value));
    };

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };
    const handleStartYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setStartYear(year)
    };
    const handleStartMonthChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let month = Number(event.target.value);
        setStartMonth(month)
    };
    const handleEndYearChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        let year = Number(event.target.value);
        setEndYear(year)
    };
    const fetchSentimentData = () => {
        if (startYear && endYear) {
            setError(undefined);
            // setInProgress(true);
            getAverageTweetsSentimentData(selectedTweetBankId, startMonth, startYear, endYear, groupBy).then(d => {
                setSentimentData(d);
                // setInProgress(false);
            }).catch(err => {
                setError(err.toString());
                // setInProgress(false);
            })
        }
    };

    const fetchSentimentDataByKeyword = () => {
        if (keyword && keyword.length > 0 && startYear && endYear) {
            setError(undefined);
            setInProgress(true);
            getTweetsSentimentData(selectedTweetBankId, keyword, startMonth, startYear, endYear, groupBy).then(d => {
                setSentimentDataByKeyword(d);
                setInProgress(false);
            }).catch(err => {
                setError(err.toString());
                setInProgress(false);
            })
        }
    };

    const average = (arr: number[]): number => {
        return arr.length > 0
            ? _.sum(arr) / arr.length
            : 0
    }

    const getArticleSentimentStatsRows = (): ArticleSentimentStatsRow[] => {
        return [{
            siteUrl: 'https://twitter.com/',
            fullUrl: 'Tweets',
            totalNumberOfPoints: sentimentData.y.length,
            totalAverageSentiment: average(sentimentData.y),
            keywordNumberOfPoints: sentimentDataByKeyword.y.length,
            keywordAverageSentiment: average(sentimentDataByKeyword.y)
        }]
    }

    let tweetBankMenuItems = allTweetBanks.map(b => <MenuItem key={b.id} value={b.id}>{b.phraseFa} ({b.phraseEn})</MenuItem>)

    let monthMenuItems = months.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)

    let yearMenuItems = _.range(2015, 2021).map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)

    const chartRef = useRef<Line>(null);
    const handleZoomReset = () => {
        (chartRef.current?.chartInstance as any).resetZoom()
    }
    return <Container maxWidth='xl'>
        <div className={styles.chartContainer}>
            <Button
                variant="outlined"
                size="small"
                className={styles.resetZoom}
                color="primary"
                disabled={inProgress}
                onClick={handleZoomReset}>
                Reset Zoom
            </Button>
            <Line data={getChartData(sentimentData, sentimentDataByKeyword)}
                  options={getChartOptions(isMobileScreen)}
                  ref={chartRef}
            />
        </div>
        {inProgress && <LinearProgress color="secondary" className={styles.progress}/>}
        {error && <span className={styles.error}>{error}</span>}
        <div className={styles.searchContainer}>
            <form className={styles.searchForm} noValidate autoComplete="off">
                <FormControl className={styles.formControl}>
                    <InputLabel id="tweet-bank-select-label">Tweets bank</InputLabel>
                    <Select
                        labelId="tweet-bank-select-label"
                        id="select-tweet-bank"
                        value={selectedTweetBankId}
                        onChange={handleTweetBankChange}
                    >{tweetBankMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="group-by-select-label">Group By</InputLabel>
                    <Select
                        labelId="group-by-select-label"
                        id="select-group-by"
                        value={groupBy}
                        onChange={handleGroupByChange}
                    >
                        <MenuItem key="day" value="day">Day</MenuItem>
                        <MenuItem key="month" value="month">Month</MenuItem>
                        <MenuItem key="year" value="year">Year</MenuItem>

                    </Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="start-month-select-label">Start Month</InputLabel>
                    <Select
                        labelId="start-month-select-label"
                        id="select-start-dt"
                        value={startMonth}
                        onChange={handleStartMonthChange}
                    >{monthMenuItems}</Select>
                </FormControl>

                <FormControl className={styles.formControl}>
                    <InputLabel id="start-dt-select-label">Start Year</InputLabel>
                    <Select
                        labelId="start-dt-select-label"
                        id="select-start-dt"
                        value={startYear}
                        onChange={handleStartYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>
                <FormControl className={styles.formControl}>
                    <InputLabel id="end-dt-select-label">End Year</InputLabel>
                    <Select
                        labelId="end-dt-select-label"
                        id="select-end-dt"
                        value={endYear}
                        onChange={handleEndYearChange}
                    >{yearMenuItems}</Select>
                </FormControl>

            </form>
            <div className={styles.searchButtonContainer}>
                <TextField
                    id="outlined-basic"
                    label="Search text"
                    variant="outlined"
                    className={styles.searchControl}
                    value={keyword}
                    onChange={handleSearchTextChange}
                    onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            fetchSentimentDataByKeyword();
                        }
                    }}
                />
                <div className={styles.searchButton}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={inProgress}
                        onClick={fetchSentimentDataByKeyword}>
                        Search
                    </Button>
                </div>
            </div>
        </div>
        <ArticleSentimentStatsTable rows={getArticleSentimentStatsRows()} showTotal={false}/>
    </Container>
}

export default TweetsSentiment;

const convertData = (s: Series): ChartPoint[] => {
    let zipped = _.zip(s.x, s.y)
    return _.map(zipped, xy => ({x: xy[0], y: xy[1]} as ChartPoint))
}

const rgbToHex = (r: number, g: number, b: number): string => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

const getChartData = (sentimentData: Series, sentimentDataByKeyword: Series): ChartData => {
    let colors = [
        rgbToHex(114, 147, 203),
        rgbToHex(57, 106, 177)
    ]
    let dataset: ChartDataSets[] = new Array<ChartDataSets>()
    dataset.push(
        {
            data: convertData(sentimentData),
            label: "Tweets",
            lineTension: 0,
            borderColor: colors[0],
            fill: false
        }, {
            data: convertData(sentimentDataByKeyword),
            label: "Tweets & keyword",
            lineTension: 0,
            borderColor: colors[1],
            fill: false
        }
    )
    return {
        datasets: dataset
    }
}

const getChartOptions = (isMobile?: boolean): ChartOptions => ({
    maintainAspectRatio: false,
    title: {
        display: true,
        text: 'Sentiment analysis'
    },
    scales: {
        xAxes: [{
            type: 'time',
            time: {
                unit: 'month',
                tooltipFormat: 'MMM DD, YYYY'
            }
        }],
        yAxes: [{
            ticks: {
                beginAtZero: true   // minimum value will be 0.
            }
        }]
    },
    legend: {
        position: isMobile ? "top" : "right",
        labels: {
            filter: (legendItem, chartData) => {
                return legendItem && legendItem.text && legendItem.text.length > 1
            }
        }
    },
    plugins: {zoom: zoomOptions}
})
